import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Lock, MapPin, Copy, Check, Search, LogOut, Loader2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { useTitle } from "@/components/PageHeader";
import { staffApi, setStaffToken, clearStaffToken, getStaffToken } from "@/lib/api";
import { createTileMap, pctToLatLng, pinIcon, TEAL } from "@/lib/mapTiles";

const RanchMap = ({ ranches, focusId }) => {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (mapRef.current || !divRef.current) return;
    const { map } = createTileMap(divRef.current, { zoomControl: true });
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    return () => { map.remove(); mapRef.current = null; markersRef.current = {}; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers(); markersRef.current = {};
    ranches.forEach((r) => {
      const m = L.marker(pctToLatLng(map, r.x, r.y), { icon: pinIcon(true) });
      m.bindPopup(
        `<div style="font-family:ui-monospace,monospace"><div style="font-size:14px;color:#e8e0d4">${r.name || "Ranch"}</div>${r.house_id ? `<div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#8F877C;margin-top:2px">House ID: ${r.house_id}</div>` : ""}${r.coords ? `<div style="font-size:11px;color:${TEAL};margin-top:6px">${r.coords}</div>` : ""}</div>`,
        { closeButton: false }
      );
      m.addTo(layer);
      markersRef.current[r.id] = m;
    });
  }, [ranches]);

  useEffect(() => {
    const map = mapRef.current, m = markersRef.current[focusId];
    if (map && m) { map.setView(m.getLatLng(), Math.max(map.getZoom(), 5), { animate: true }); m.openPopup(); }
  }, [focusId]);

  return <div ref={divRef} data-testid="staff-ranch-map" className="w-full h-full min-h-[520px] bg-[#141210]" style={{ zIndex: 0 }} />;
};

const RanchRow = ({ r, onFocus }) => {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => { e.stopPropagation(); await navigator.clipboard.writeText(r.coords || ""); setCopied(true); toast.success("Coords copied"); setTimeout(() => setCopied(false), 1600); };
  return (
    <div role="button" tabIndex={0} onClick={() => onFocus(r.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFocus(r.id); } }}
      data-testid={`ranch-row-${r.id}`}
      className="w-full text-left border border-[#2A201A] bg-surface/30 hover:border-teal/50 p-3 transition-colors cursor-pointer">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-display text-lg font-light text-bone leading-tight">{r.name}</p>
          {r.house_id && <p className="font-mono text-[10px] uppercase tracking-wider text-[#8F877C] mt-0.5">House ID: {r.house_id}</p>}
        </div>
        <MapPin className="h-4 w-4 text-teal shrink-0 mt-1" />
      </div>
      {r.coords && (
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate bg-obsidian border border-[#2A201A] px-2 py-1 font-mono text-[11px] text-teal">{r.coords}</code>
          <button onClick={copy} data-testid={`ranch-copy-${r.id}`} title="Copy coordinates"
            className="inline-flex items-center gap-1 font-feature text-[10px] uppercase tracking-wider bg-teal text-obsidian px-2.5 py-1.5 hover:bg-bone transition-colors">
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </button>
        </div>
      )}
    </div>
  );
};

const HouseRow = ({ h }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(h.coords || ""); setCopied(true); toast.success("Coords copied"); setTimeout(() => setCopied(false), 1600); };
  return (
    <div data-testid={`house-row-${h.id}`} className="border border-[#2A201A] bg-surface/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-display text-lg font-light text-bone">House {h.house_id}</p>
        <MapPin className="h-4 w-4 text-teal shrink-0" />
      </div>
      {h.coords ? (
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate bg-obsidian border border-[#2A201A] px-2 py-1 font-mono text-[11px] text-teal">{h.coords}</code>
          <button onClick={copy} data-testid={`house-copy-${h.id}`} title="Copy coordinates"
            className="inline-flex items-center gap-1 font-feature text-[10px] uppercase tracking-wider bg-teal text-obsidian px-2.5 py-1.5 hover:bg-bone transition-colors">
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </button>
        </div>
      ) : (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-[#5c554c]">No coords on record</p>
      )}
    </div>
  );
};

export default function StaffPanel() {
  useTitle("Staff Ops");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [ranches, setRanches] = useState([]);
  const [houses, setHouses] = useState([]);
  const [layer, setLayer] = useState("ranches");
  const [q, setQ] = useState("");
  const [focusId, setFocusId] = useState(null);

  const loadData = useCallback(async () => {
    try { const { data } = await staffApi.get("/ranches"); setRanches(data); } catch { /* noop */ }
    try { const { data } = await staffApi.get("/houses"); setHouses(data); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      if (!getStaffToken()) { setChecking(false); return; }
      try { await staffApi.get("/staff/verify"); setAuthed(true); await loadData(); }
      catch { clearStaffToken(); }
      finally { setChecking(false); }
    })();
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff/login", { password: pw });
      setStaffToken(data.token); setAuthed(true); setPw(""); await loadData();
    } catch { toast.error("Incorrect password"); }
    finally { setBusy(false); }
  };
  const logout = () => { clearStaffToken(); setAuthed(false); setRanches([]); setHouses([]); };

  const filtered = useMemo(
    () => ranches.filter((r) => `${r.name} ${r.house_id}`.toLowerCase().includes(q.toLowerCase())),
    [ranches, q]
  );
  const filteredHouses = useMemo(
    () => houses.filter((h) => String(h.house_id).toLowerCase().includes(q.toLowerCase())),
    [houses, q]
  );

  if (checking) return <div className="pt-40 pb-40 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  if (!authed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-16">
        <form onSubmit={login} data-testid="staff-login-form" className="w-full max-w-sm border border-[#2A201A] bg-surface/50 p-8">
          <Lock className="h-7 w-7 text-teal" />
          <h1 className="mt-5 font-display text-3xl font-light">Staff Ops</h1>
          <p className="mt-2 text-sm text-[#8F877C]">Enter the staff password to access the ranch map.</p>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="staff-password-input"
            placeholder="Staff password" autoFocus
            className="mt-6 w-full bg-obsidian border border-[#2A201A] px-4 py-3 text-bone focus:outline-none focus:border-teal" />
          <button type="submit" disabled={busy || !pw} data-testid="staff-login-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian py-3 hover:bg-bone transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <p className="font-feature text-xs uppercase tracking-[0.3em] text-teal">Staff Ops</p>
          <h1 className="font-display text-4xl font-light">{layer === "ranches" ? "Ranch Map" : "House Directory"}</h1>
        </div>
        <button onClick={logout} data-testid="staff-logout-btn" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-4 py-2.5 text-[#8F877C] hover:border-teal hover:text-teal transition-colors"><LogOut className="h-4 w-4" /> Lock</button>
      </div>

      <div className="flex gap-2 mb-6">
        {[["ranches", `Ranches (${ranches.length})`], ["houses", `Houses (${houses.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => { setLayer(id); setQ(""); }} data-testid={`staff-layer-${id}`}
            className={`font-feature text-xs uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${layer === id ? "border-teal bg-teal text-obsidian" : "border-[#2A201A] text-[#8F877C] hover:border-teal/50 hover:text-bone"}`}>
            {label}
          </button>
        ))}
      </div>

      {layer === "ranches" ? (
        <div className="grid gap-6 lg:grid-cols-[320px_1fr] items-start">
          <aside className="space-y-3" data-testid="ranch-directory">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5c554c]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${ranches.length} ranches…`} data-testid="ranch-search"
                className="w-full bg-obsidian border border-[#2A201A] pl-10 pr-4 py-2.5 text-sm text-bone focus:outline-none focus:border-teal" />
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {filtered.map((r) => <RanchRow key={r.id} r={r} onFocus={setFocusId} />)}
              {ranches.length === 0 && <p className="font-mono text-xs uppercase tracking-widest text-[#5c554c] py-6">No ranches added yet. Add them in the Admin panel → Ranches.</p>}
              {ranches.length > 0 && filtered.length === 0 && <p className="font-mono text-xs text-[#5c554c] py-4">No matches.</p>}
            </div>
          </aside>
          <div className="border border-[#2A201A] overflow-hidden">
            <RanchMap ranches={ranches} focusId={focusId} />
          </div>
        </div>
      ) : (
        <div data-testid="house-directory">
          <div className="relative max-w-md mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5c554c]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${houses.length} houses by ID…`} data-testid="house-search"
              className="w-full bg-obsidian border border-[#2A201A] pl-10 pr-4 py-2.5 text-sm text-bone focus:outline-none focus:border-teal" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {filteredHouses.map((h) => <HouseRow key={h.id} h={h} />)}
          </div>
          {houses.length === 0 && <p className="font-mono text-xs uppercase tracking-widest text-[#5c554c] py-6">No houses imported yet.</p>}
          {houses.length > 0 && filteredHouses.length === 0 && <p className="font-mono text-xs text-[#5c554c] py-4">No matches.</p>}
        </div>
      )}
    </div>
  );
}
